import { useState } from 'react'

import isEqual from 'lodash/isEqual'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import {
  DEFAULT_ASSIGNMENT_COST_CENTER_ID,
  DEFAULT_ASSIGNMENT_PERCENTAGE,
  defaultAssignment,
  defaultStructuredAssignment,
} from 'modules/cost_centers/components/CostCenterAssigments/lib/defaults'
import {
  CostCenterAssignment,
  FormattedAssignment,
  FormattedAssignmentWithDeletion,
} from 'modules/cost_centers/components/CostCenterAssigments/lib/types'

import { CostCenterOption } from 'modules/cost_centers/components/CostCenterAssigments/components/AssignmentsManager/Picker/hooks/useCostCenterByLegalEntities'

import { useAssignmentsValidator } from './useAssignmentsValidator'
import { DateRange } from './validations/overlaps'

interface ValueAssignment {
  name: string
  id: string
  percentage: number
}

interface Props {
  currentAssignments: (FormattedAssignmentWithDeletion | FormattedAssignment)[]
  allAssignments: CostCenterAssignment[]
  defaultDateRange?: { startDate: string; endDate: string }
  hasNewStructureEnabled?: boolean
}

export const useAssignmentsManager = ({
  currentAssignments,
  allAssignments,
  defaultDateRange,
  hasNewStructureEnabled = false,
}: Props) => {
  const [assignmentToCreateTemplate, setAssignmentToCreateTemplate] = useState<
    FormattedAssignment[]
  >([])
  // We want to disable the button when the user has not interacted with the component
  const [userInteracted, setUserInteracted] = useState(false)
  const [assignmentsToCreate, setAssignmentsToCreate] = useState<FormattedAssignment[]>([])
  const [assignmentsToUpdate, setAssignmentsToUpdate] =
    useState<FormattedAssignmentWithDeletion[]>(currentAssignments)
  const [assignments, setAssignments] = useState<FormattedAssignment[]>([
    ...assignmentsToCreate,
    ...assignmentsToUpdate,
  ])

  const assignmentsToUpdateNotMarkedForDeletion = assignmentsToUpdate.filter(
    (v) => !v.markedForDeletion
  )

  useUnsafeEffect(() => {
    if (userInteracted) return

    const assignmentsAreDifferent = !isEqual(assignments, [
      ...assignmentsToCreate,
      ...assignmentsToUpdate,
    ])

    if (assignmentsAreDifferent) {
      setUserInteracted(true)
    }
  }, [assignments, userInteracted, assignmentsToCreate, assignmentsToUpdate])

  //------------ Date Range ------------

  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({
    startDate: currentAssignments[0]?.startDate || defaultDateRange?.startDate || '',
    endDate: currentAssignments[0]?.endDate || defaultDateRange?.endDate || '',
  })

  //------------ Validations ------------

  const { validForSave, validations } = useAssignmentsValidator({
    selectedDateRange,
    currentAssignments: currentAssignments,
    displayedAssignments: assignments,
    allAssignments,
    userInteracted: userInteracted,
    hasNewStructureEnabled,
  })

  //------------ Date Range Management ------------

  const setNewDateRange = ({ startDate, endDate }: { startDate?: string; endDate?: string }) => {
    const dateToAssign = {
      ...(startDate ? { startDate } : {}),
      ...(typeof endDate === 'string' ? { endDate } : {}),
    }

    setSelectedDateRange((prevDateRange) => ({
      ...prevDateRange,
      ...dateToAssign,
    }))

    setAssignmentToCreateTemplate((prevAssignments) => {
      return prevAssignments.map((assignment) => ({
        ...assignment,
        ...dateToAssign,
      }))
    })

    setAssignmentsToCreate((prevAssignments) => {
      return prevAssignments.map((assignment) => ({
        ...assignment,
        ...dateToAssign,
      }))
    })

    setAssignmentsToUpdate((prevAssignments) => {
      return prevAssignments.map((assignment) => ({
        ...assignment,
        ...dateToAssign,
      }))
    })
  }

  //------------ Add New Assignment ------------

  const addNewAssignment = useUnsafeCallback(() => {
    const newAssignment = defaultAssignment({
      startDate: String(selectedDateRange.startDate),
      endDate: selectedDateRange.endDate,
      previousCostCenterId: Math.min(...assignments.map((item) => Number(item.costCenterId))),
    })

    setAssignmentToCreateTemplate((prevAssignments) => [...prevAssignments, newAssignment])
    setAssignments([...assignments, newAssignment])

    track(trackingEvents.accounting.addNewCostCenterToAssignment, {
      countAdded:
        assignments.filter((item) => item.percentage === DEFAULT_ASSIGNMENT_PERCENTAGE).length + 1,
    })
  }, [assignments, selectedDateRange, setAssignmentToCreateTemplate, setAssignments])

  //------------ Delete Assignment ------------

  const deleteValue = (value: ValueAssignment) => {
    const storedAssignment = assignmentsToUpdate.find((v) => String(v.costCenterId) === value.id)
    const assignmentInCreate = assignmentsToCreate.find((v) => String(v.costCenterId) === value.id)

    if (storedAssignment) {
      setAssignmentsToUpdate([
        ...assignmentsToUpdate.filter((v) => v.id !== storedAssignment.id),
        { ...storedAssignment, markedForDeletion: true },
      ])
    }

    if (assignmentInCreate) {
      setAssignmentsToCreate([
        ...assignmentsToCreate.filter((v) => v.costCenterId !== Number(value.id)),
      ])
    }

    setAssignments([...assignments.filter((v) => String(v.costCenterId) !== value.id)])
  }

  const manageCostcenterInAssignmentToCreateInTemplate = (
    value: FormattedAssignment,
    newValue: ValueAssignment
  ) => {
    const { costCenterId } = value

    if (
      newValue.percentage === DEFAULT_ASSIGNMENT_PERCENTAGE ||
      Number(newValue.id) <= DEFAULT_ASSIGNMENT_COST_CENTER_ID
    ) {
      setAssignmentToCreateTemplate([
        ...assignmentToCreateTemplate.filter((v) => v.costCenterId !== costCenterId),
        { ...value, ...newValue, costCenterId: Number(newValue.id) },
      ])
    } else {
      setAssignmentToCreateTemplate([
        ...assignmentToCreateTemplate.filter((v) => v.costCenterId !== costCenterId),
      ])

      setAssignmentsToCreate([
        ...assignmentsToCreate,
        { ...value, ...newValue, costCenterId: Number(newValue.id) },
      ])
    }
  }

  //------------ Update Assignment ------------

  const updateAssignment = (valueId: string, newValue: ValueAssignment) => {
    const assignment = assignments.find((v) => v.costCenterId == Number(valueId))

    if (!assignment) return

    const costCenterInUpdateAssignments = assignmentsToUpdateNotMarkedForDeletion.find(
      (v) => v.costCenterId == Number(valueId)
    )

    const costCenterInAssignmentToCreateInTemplate = assignmentToCreateTemplate.find(
      (v) => v.costCenterId == Number(valueId)
    )

    if (costCenterInUpdateAssignments) {
      setAssignmentsToUpdate([
        ...assignmentsToUpdate.filter((v) => v.id !== costCenterInUpdateAssignments.id),
        {
          ...costCenterInUpdateAssignments,
          percentage: newValue.percentage,
          costCenterId: Number(newValue.id),
        },
      ])
    } else if (costCenterInAssignmentToCreateInTemplate) {
      manageCostcenterInAssignmentToCreateInTemplate(
        costCenterInAssignmentToCreateInTemplate,
        newValue
      )
    } else {
      const assignmentToPatch = assignmentsToCreate.find((v) => v.costCenterId == Number(valueId))

      if (!assignmentToPatch) return

      setAssignmentsToCreate([
        ...assignmentsToCreate.filter((v) => v.costCenterId !== Number(newValue.id)),
        { ...assignmentToPatch, ...newValue, costCenterId: Number(newValue.id) },
      ])
    }

    // In order to not to update the order of the assignments, we need to map the assignments and
    //update the assignment with the new value
    setAssignments(
      assignments.map((item) =>
        String(item.costCenterId) === valueId
          ? {
              ...item,
              ...newValue,
              costCenterId: Number(newValue.id),
            }
          : item
      )
    )
  }

  //------------ Structured Mode Functions ------------

  const addNewStructuredAssignment = useUnsafeCallback(() => {
    const newAssignment = defaultStructuredAssignment({
      startDate: String(selectedDateRange.startDate),
      endDate: selectedDateRange.endDate,
    })

    setAssignmentToCreateTemplate((prevAssignments) => [...prevAssignments, newAssignment])
    setAssignments([...assignments, newAssignment])

    track(trackingEvents.accounting.addNewCostCenterToAssignment, {
      countAdded:
        assignments.filter((item) => item.percentage === DEFAULT_ASSIGNMENT_PERCENTAGE).length + 1,
    })
  }, [assignments, selectedDateRange, setAssignmentToCreateTemplate, setAssignments])

  const deleteStructuredAssignment = (assignmentId: string) => {
    const storedAssignment = assignmentsToUpdate.find((v) => v.id === assignmentId)
    const assignmentInCreate = assignmentsToCreate.find((v) => v.id === assignmentId)
    const assignmentInTemplate = assignmentToCreateTemplate.find((v) => v.id === assignmentId)

    if (storedAssignment) {
      setAssignmentsToUpdate([
        ...assignmentsToUpdate.filter((v) => v.id !== storedAssignment.id),
        { ...storedAssignment, markedForDeletion: true },
      ])
    }

    if (assignmentInCreate) {
      setAssignmentsToCreate([...assignmentsToCreate.filter((v) => v.id !== assignmentId)])
    }

    if (assignmentInTemplate) {
      setAssignmentToCreateTemplate([
        ...assignmentToCreateTemplate.filter((v) => v.id !== assignmentId),
      ])
    }

    setAssignments([...assignments.filter((v) => v.id !== assignmentId)])
  }

  const updateStructuredField = (
    assignmentId: string,
    updates: Partial<FormattedAssignment>
  ) => {
    const assignment = assignments.find((v) => v.id === assignmentId)
    if (!assignment) return

    const updatedAssignment = { ...assignment, ...updates }

    // Check if this is in the update list (existing assignment)
    const existingInUpdate = assignmentsToUpdate.find((v) => v.id === assignmentId)
    if (existingInUpdate) {
      setAssignmentsToUpdate(
        assignmentsToUpdate.map((v) =>
          v.id === assignmentId ? { ...v, ...updates } : v
        )
      )
    }

    // Check if this is in the template (new assignment not yet valid)
    const existingInTemplate = assignmentToCreateTemplate.find((v) => v.id === assignmentId)
    if (existingInTemplate) {
      const updatedTemplateAssignment = { ...existingInTemplate, ...updates }
      
      // Check if assignment is now valid (has at least one cost center and valid percentage)
      const hasValidCostCenter =
        updatedTemplateAssignment.jobCatalogCostCenterId != null ||
        updatedTemplateAssignment.workplaceCostCenterId != null
      const hasValidPercentage =
        updatedTemplateAssignment.percentage !== DEFAULT_ASSIGNMENT_PERCENTAGE &&
        updatedTemplateAssignment.percentage > 0

      if (hasValidCostCenter && hasValidPercentage) {
        // Move from template to create list
        setAssignmentToCreateTemplate(
          assignmentToCreateTemplate.filter((v) => v.id !== assignmentId)
        )
        setAssignmentsToCreate([...assignmentsToCreate, updatedTemplateAssignment])
      } else {
        // Keep in template
        setAssignmentToCreateTemplate(
          assignmentToCreateTemplate.map((v) =>
            v.id === assignmentId ? updatedTemplateAssignment : v
          )
        )
      }
    }

    // Check if this is in the create list
    const existingInCreate = assignmentsToCreate.find((v) => v.id === assignmentId)
    if (existingInCreate) {
      setAssignmentsToCreate(
        assignmentsToCreate.map((v) =>
          v.id === assignmentId ? { ...v, ...updates } : v
        )
      )
    }

    // Update the display list
    setAssignments(
      assignments.map((item) => (item.id === assignmentId ? updatedAssignment : item))
    )
  }

  const updateJobStructure = (assignmentId: string, option: CostCenterOption | null) => {
    // If selecting a new cost center, remove it from any other assignments
    if (option) {
      const otherAssignments = assignments.filter((a) => a.id !== assignmentId)
      otherAssignments.forEach((otherAssignment) => {
        if (otherAssignment.jobCatalogCostCenterId === Number(option.id)) {
          // Remove the job structure from the other assignment
          updateStructuredField(otherAssignment.id, {
            jobCatalogCostCenterId: undefined,
            jobCatalogNodeUuid: undefined,
          })
        }
      })
    }

    updateStructuredField(assignmentId, {
      jobCatalogCostCenterId: option ? Number(option.id) : undefined,
      jobCatalogNodeUuid: option?.jobCatalogNodeUuid ?? undefined,
      name: option?.name,
    })
  }

  const updateWorkplace = (assignmentId: string, option: CostCenterOption | null) => {
    // If selecting a new cost center, remove it from any other assignments
    if (option) {
      const otherAssignments = assignments.filter((a) => a.id !== assignmentId)
      otherAssignments.forEach((otherAssignment) => {
        if (otherAssignment.workplaceCostCenterId === Number(option.id)) {
          // Remove the workplace from the other assignment
          updateStructuredField(otherAssignment.id, {
            workplaceCostCenterId: undefined,
            workplaceId: undefined,
          })
        }
      })
    }

    updateStructuredField(assignmentId, {
      workplaceCostCenterId: option ? Number(option.id) : undefined,
      workplaceId: option?.workplaceId ?? undefined,
      name: option?.name,
    })
  }

  const updateStructuredPercentage = (assignmentId: string, percentage: number) => {
    updateStructuredField(assignmentId, { percentage })
  }

  return {
    assignmentsToCreate,
    assignmentsToUpdate,
    selectedDateRange,
    assignments,
    validForSave,
    validations,
    // Legacy mode functions
    addNewAssignment,
    deleteValue,
    updateAssignment,
    // Structured mode functions
    addNewStructuredAssignment,
    deleteStructuredAssignment,
    updateJobStructure,
    updateWorkplace,
    updateStructuredPercentage,
    // Shared functions
    setAssignmentsToCreate,
    setAssignmentsToUpdate,
    setNewDateRange,
  }
}
